import { Button, Card, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { isRootCauseWorkspace, RootCauseWorkspaceType } from "..";
import { BaselineIcon, BoxIcon, ComparisonIcon, CopyFromDowIcon, CopyFromUpIcon } from "../../../../assets/icons";
import PeriodSelector from "../../../../components/common/PeriodSelector";
import { FilterContexType } from "../../../../components/layout/ViewHeader";
import { useFiltersRegistry } from "../../../../context/FiltersProvider";
import { FilterDefinition } from "../../../../context/FiltersProvider/types";
import { UserPeriodFilter } from "../../../../state/company";
import { RootCauseCardData } from "../data";
import { CardDashboard } from "./components/CardDashboard";
import { RootCauseFiltersMenu } from "./components/menu/RootCauseFiltersMenu";

export const getRootCauseCardIcon = (id: "perimeter" | "baseline" | "comparison") => {
	switch (id) {
		case "perimeter":
			return <BoxIcon />;
		case "baseline":
			return <BaselineIcon />;
		case "comparison":
			return <ComparisonIcon />;
		default:
			return null;
	}
};

interface Props extends RootCauseCardData {
	periodFilter?: UserPeriodFilter;
	setPeriodFilter?: (filter: UserPeriodFilter) => void;
	filterContext: FilterContexType;
}

export const RootCauseCard = ({ id, availableActions, periodFilter, setPeriodFilter, filterContext }: Props) => {
	const { t } = useTranslation("whatif.rootcause");
	const {
		rootCauseState,
		addRootCausePerimeterFilter,
		addRootCauseDashboardFilter,
		addRootCauseSavedFilter,
		applyRootCauseConfiguration,
		openRootCauseAdvancedModeModal,
		copyRootCauseWorkspace,
	} = useFiltersRegistry();

	const cardTitle = t(`cardTitles/${id}`);
	const cardIcon = getRootCauseCardIcon(id);

	const handleFilterSelect = (filter: FilterDefinition) => {
		// PERIMETER
		if (id === "perimeter") {
			addRootCausePerimeterFilter({
				id: filter.id,
				label: filter.name,
			});

			return;
		}

		// AVAILABLE FILTER
		if (filter.source === "system" && filter.filterType === "simple") {
			addRootCauseDashboardFilter(id, filter.id);

			return;
		}

		// SAVED FILTER
		if (filter.source === "user" && filter.filterType === "simple") {
			addRootCauseSavedFilter(id, filter);

			return;
		}

		// CONFIGURATION
		if (filter.filterType === "configuration") {
			const workspace = id === "baseline" ? rootCauseState.baseline : rootCauseState.comparison;

			const hasSimpleFilters = workspace.dashboardFilters.length > 0;

			if (hasSimpleFilters) {
				openRootCauseAdvancedModeModal(id, filter);
				return;
			}

			applyRootCauseConfiguration(id, filter.filter);

			return;
		}
	};

	const oppositeWorkspace: RootCauseWorkspaceType | null =
		id === "baseline" ? "comparison" : id === "comparison" ? "baseline" : null;

	const canCopy =
		oppositeWorkspace !== null &&
		(rootCauseState[oppositeWorkspace].dashboardFilters.length > 0 ||
			rootCauseState[oppositeWorkspace].advancedForm.length > 0);

	return (
		<Card sx={{ padding: 2, overflow: "visible" }}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom={2}>
				<Stack direction="row" spacing={1} alignItems="center">
					{cardIcon}
					<Typography variant="h6" fontWeight={500}>
						{cardTitle}
					</Typography>
					{availableActions.includes("copy") && isRootCauseWorkspace(id) && oppositeWorkspace && (
						<Button
							sx={{ ml: 1 }}
							size="small"
							disabled={!canCopy}
							onClick={() => copyRootCauseWorkspace(oppositeWorkspace, id)}
							startIcon={oppositeWorkspace === "comparison" ? <CopyFromDowIcon /> : <CopyFromUpIcon />}
						>
							{t("copy terms", { cardType: oppositeWorkspace })}
						</Button>
					)}
				</Stack>
				<Stack direction="row" spacing={1} alignItems="center">
					{availableActions.includes("criteria") && (
						<RootCauseFiltersMenu cardId={id} filterContext={filterContext} onSelect={handleFilterSelect} />
					)}
					{availableActions.includes("period") && setPeriodFilter && (
						<PeriodSelector filter={periodFilter} setFilter={setPeriodFilter} variant="simple" />
					)}
					{availableActions.includes("filters") && id !== "perimeter" && (
						<RootCauseFiltersMenu cardId={id} filterContext={filterContext} onSelect={handleFilterSelect} />
					)}
				</Stack>
			</Stack>
			<CardDashboard cardType={id} filterContext={filterContext} />
		</Card>
	);
};
